import { create } from 'zustand';
import { persist as persistMiddleware, devtools } from 'zustand/middleware';
import { createSlice, StoreType } from './slice';

/**
 * Configuration options for creating a store.
 */
type CreateStoreConfiguration<T> = {
  /** The name of the persisted store (used in localStorage). */
  name?: string;

  /** The initial data for the store. */
  initialData?: T;

  /** Whether to enable caching (localStorage persistence). Defaults to true. */
  persist?: boolean;

  /** Whether to enable devtools for the store (useful for debugging). Defaults to false. */
  enableDevTools?: boolean;
};

/**
 * Creates a Zustand store with optional caching and devtools support.
 *
 * @param config - The configuration for the store.
 * @returns The created Zustand store with methods to interact with the state.
 *
 * @example
 * const useStore = createStore({
 *   initialData: { user: { name: 'John' } },
 *   enableDevTools: true,
 * });
 *
 * // Usage of the useStore
 * const { data, get, update, reset } = useStore();
 * const userName = get('user.name', 'Guest'); // Get the value from the store at the given path('user.name'), with a default value('Guest') if not found
 * update('user.name', 'Doe'); // Update the value of a property in the store at the given path('user.name') with value('Doe')
 * reset(['user.name']); // this will reset the store except the path value 'user.name'
 */
export const createStore = <T>(config?: CreateStoreConfiguration<T>) => {
  const {
    name = 'report-it-storage',
    initialData,
    persist = true,
    enableDevTools,
  } = config || {};

  // Create the slice with the configuration passed in
  const createSliceWithConfig = (set: any, get: any, store: any) => ({
    ...createSlice<T>(initialData)(set, get, store),
  });

  // Apply persistence if enabled
  let slice = createSliceWithConfig;
  if (persist) {
    slice = persistMiddleware(createSliceWithConfig, {
      name,
      getStorage: () => sessionStorage, // store the data in sessionStorage so that data is cleared on tab close (cannot be accessed across tabs)
    });
  }

  // Apply devtools if enabled
  if (enableDevTools) {
    slice = devtools(slice);
  }

  // Return the Zustand store created with the slice
  return create<StoreType<T>>()(slice);
};
